/**
 * Editor marketing/demo landing page — showcases what PulseStudio's editor
 * can do, with 2 example ad galleries (credit card, concrete pavers) driven
 * by the same backend showcase data that powers the home-page reel grid.
 *
 * This is a product sub-page so that users who click "Editor" in the nav
 * land on a focused page (not the full landing) and can immediately try
 * the editor with one click.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as fabric from "fabric";
import {
  Sparkles, Wand2, Film, Clapperboard, Image as ImageIcon,
  CheckCircle2, ArrowRight, Play, CreditCard, Building2, Flame,
} from "lucide-react";
import { LandingHeader } from "../landing/LandingHeader";
import { LandingFooter } from "../landing/LandingFooter";
import { DEFAULT_LANGUAGES } from "../landing/constants";
import DirectorShowcase from "../ads-creator/DirectorShowcase";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { adsApi } from "../ads-creator/api";

export default function EditorShowcasePage() {
  const { t, language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const [showcases, setShowcases] = useState([]);
  useEffect(() => {
    adsApi.get("/ai/showcase")
      .then((r) => setShowcases(r.data.showcases || []))
      .catch(() => {});
  }, []);
  // Pick the three demo showcases we want to feature up top.
  const featured = {
    creditCard: showcases.find((s) => s.id === "credit-card-launch"),
    concretePaver: showcases.find((s) => s.id === "concrete-tile-ai"),
    yoga: showcases.find((s) => s.id === "yoga-first-free"),
  };

  return (
    <div className="bg-[var(--color-bg)] text-[var(--color-text)]">
      <LandingHeader t={t} user={user} language={language} setLanguage={setLanguage} languages={DEFAULT_LANGUAGES} />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6" data-testid="editor-hero">
        <div className="max-w-6xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#a855f7]/15 text-[#c084fc] text-[11px] font-black uppercase tracking-wider mb-5">
            <Sparkles className="w-3 h-3" /> {t.editor_page_eyebrow || "PulseStudio Editor"}
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.editor_page_title_a || "Professional ad studio"}<br />
            <span className="text-[#a855f7]">{t.editor_page_title_b || "in your browser"}</span>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto mb-8 leading-relaxed">
            {t.editor_page_sub || "Multi-scene video editor, AI reel director, photo-to-ad composer. Ship scroll-stopping creative without hiring an agency."}
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/ads-creator"
              data-testid="editor-cta-open"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold hover:shadow-lg transition-shadow"
            >
              <Play className="w-4 h-4" /> {t.editor_page_cta_open || "Open the editor"}
            </Link>
            <Link
              to="/ads-creator/photo-start"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[var(--color-border)] font-bold hover:bg-[var(--color-surface)] transition-colors"
              data-testid="editor-cta-photo"
            >
              <ImageIcon className="w-4 h-4" /> {t.editor_page_cta_photo || "Start from a photo"}
            </Link>
          </div>
        </div>
      </section>

      {/* Two featured examples — credit card + concrete pavers */}
      <section className="py-12 px-6 border-t border-[var(--color-border)]" data-testid="editor-examples">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#a855f7] font-['JetBrains_Mono'] text-xs tracking-[0.3em] uppercase mb-3">
              {t.editor_examples_eyebrow || "REAL EXAMPLES"}
            </p>
            <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] tracking-tight">
              {t.editor_examples_title || "Made with our editor"}
            </h2>
            <p className="text-[var(--color-text-muted)] mt-3 max-w-xl mx-auto">
              {t.editor_examples_sub || "Three ads built end-to-end in the editor — click any reel to open it as your starting template."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ExampleCard
              icon={CreditCard}
              accent="#c9a227"
              title={t.editor_ex_card_title || "Metal credit card · 3% cashback"}
              bullets={[
                t.editor_ex_card_b1 || "4-scene storytelling reel (hook → problem → proof → CTA)",
                t.editor_ex_card_b2 || "Premium gold-on-black color grade",
                t.editor_ex_card_b3 || "AI-written headlines · auto-timed 10s",
              ]}
              data={featured.creditCard}
              cta={t.editor_use_template || "Use this as my template"}
              testid="editor-example-card"
            />
            <ExampleCard
              icon={Building2}
              accent="#d97706"
              title={t.editor_ex_paver_title || "AI-designed concrete pavers"}
              bullets={[
                t.editor_ex_paver_b1 || "Photo-to-ad generated from a yard photo",
                t.editor_ex_paver_b2 || "Warm amber grade · bold typography",
                t.editor_ex_paver_b3 || "Zoom-preset animation · frost-proof messaging",
              ]}
              data={featured.concretePaver}
              cta={t.editor_use_template || "Use this as my template"}
              testid="editor-example-paver"
            />
            <ExampleCard
              icon={Flame}
              accent="#f97a8c"
              title={t.editor_ex_yoga_title || "Yoga studio · first class free"}
              bullets={[
                t.editor_ex_yoga_b1 || "Warm studio aesthetic · pink / cream palette",
                t.editor_ex_yoga_b2 || "Local-business angle (Bratislava-ready copy)",
                t.editor_ex_yoga_b3 || "Stagger animation · calm, breath-led pacing",
              ]}
              data={featured.yoga}
              cta={t.editor_use_template || "Use this as my template"}
              testid="editor-example-yoga"
            />
          </div>
        </div>
      </section>

      {/* Feature ladder */}
      <section className="py-16 px-6 border-t border-[var(--color-border)]" data-testid="editor-features">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Wand2, title: t.editor_f1_t || "AI Reel Director", desc: t.editor_f1_d || "One prompt → complete 4-scene reel in 10 seconds." },
              { icon: ImageIcon, title: t.editor_f2_t || "Photo → Ad", desc: t.editor_f2_d || "Upload a photo; Vision AI writes copy that fits the image." },
              { icon: Clapperboard, title: t.editor_f3_t || "Multi-scene Timeline", desc: t.editor_f3_d || "Story-arc sequencing with zoom/stagger/fast-cut presets." },
              { icon: Film, title: t.editor_f4_t || "Epic Scenes", desc: t.editor_f4_d || "Nano Banana places your subject into Times Square, stadium, cinema." },
            ].map((f, i) => (
              <div key={i} className="p-5 bg-[var(--color-surface)] border border-[var(--color-border)]">
                <div className="w-10 h-10 flex items-center justify-center bg-[#a855f7]/15 mb-3">
                  <f.icon className="w-5 h-5 text-[#c084fc]" />
                </div>
                <h3 className="font-bold text-sm mb-1.5">{f.title}</h3>
                <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full gallery (existing DirectorShowcase) */}
      <section className="py-16 px-6 border-t border-[var(--color-border)]">
        <div className="max-w-6xl mx-auto text-center mb-8">
          <p className="text-[#a855f7] font-['JetBrains_Mono'] text-xs tracking-[0.3em] uppercase mb-2">
            {t.editor_gallery_eyebrow || "BEST OF"}
          </p>
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] tracking-tight">
            {t.editor_gallery_title || "Browse the full gallery"}
          </h2>
        </div>
        <DirectorShowcase t={t} />
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 border-t border-[var(--color-border)] text-center" data-testid="editor-final-cta">
        <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-4">
          {t.editor_final_title || "Ready to build your first reel?"}
        </h2>
        <p className="text-[var(--color-text-muted)] max-w-xl mx-auto mb-6">
          {t.editor_final_sub || "3 free credits. No card. Open the editor now and see what AI can do for your brand."}
        </p>
        <Link
          to="/ads-creator"
          data-testid="editor-cta-final"
          className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold text-lg hover:shadow-xl transition-shadow"
        >
          <Sparkles className="w-5 h-5" />
          {t.editor_final_cta || "Open editor — free"}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </section>

      <LandingFooter t={t} />
    </div>
  );
}

function ExampleCard({ icon: Icon, accent, title, bullets, data, cta, testid }) {
  return (
    <div
      className="border rounded-lg overflow-hidden transition-all hover:-translate-y-0.5"
      style={{ borderColor: "var(--color-border)", background: "var(--color-surface)" }}
      data-testid={testid}
    >
      {/* Auto-play reel preview — vertical 9:16 reel, centered in card. */}
      <div
        className="w-full relative overflow-hidden flex items-center justify-center mx-auto"
        style={{
          maxWidth: "340px",
          background: data?.colors?.bg || "#0a0a12",
          backgroundImage: data ? undefined : `linear-gradient(135deg, ${accent}22, transparent)`,
          aspectRatio: data ? undefined : "9 / 16",
        }}
      >
        {data ? (
          <AutoPlayReel data={data} accent={accent} />
        ) : (
          <Icon className="w-14 h-14" style={{ color: accent, opacity: 0.5 }} />
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-8 h-8 flex items-center justify-center rounded"
            style={{ background: `${accent}22` }}
          >
            <Icon className="w-4 h-4" style={{ color: accent }} />
          </div>
          <h3 className="font-bold text-base">{title}</h3>
        </div>
        <ul className="space-y-1.5 mb-4">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text-muted)]">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/ads-creator"
          className="inline-flex items-center gap-1 text-sm font-bold hover:gap-1.5 transition-all"
          style={{ color: accent }}
        >
          {cta} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

/**
 * AutoPlayReel — stacks all scene canvases for a showcase and cycles through
 * them every 850ms with a CSS opacity crossfade. No real video encoding — it's
 * an "MP4-like" trailer driven by Fabric StaticCanvas (same technique as the
 * landing DirectorShowcase). This gives the /editor page an instant "wow"
 * moment instead of a static image, which meaningfully lifts the CTA CTR.
 */
function AutoPlayReel({ data, accent }) {
  const scenes = data?.scenes || [];
  const total = scenes.length || 1;
  const [frame, setFrame] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || total < 2) return;
    const id = setInterval(() => setFrame((f) => (f + 1) % total), 1000);
    return () => clearInterval(id);
  }, [paused, total]);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={`editor-reel-${data.id}`}
    >
      {scenes.map((sc, i) => (
        <div
          key={sc.scene_id || i}
          className={
            "transition-opacity duration-500 " +
            (i === 0 ? "relative" : "absolute inset-0")
          }
          style={{ opacity: i === frame ? 1 : 0 }}
        >
          <SceneCanvas canvasJson={sc.canvas_json} />
        </div>
      ))}
      {/* Progress bar — one tiny segment per scene, filling in sync. */}
      <div className="absolute top-2 left-2 right-2 flex gap-1 z-10" aria-hidden="true">
        {scenes.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 rounded-full overflow-hidden bg-white/20">
            <div
              className="h-full transition-all duration-500 ease-linear"
              style={{
                width: i <= frame ? "100%" : "0%",
                background: accent,
              }}
            />
          </div>
        ))}
      </div>
      {/* Scene counter badge */}
      <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-['JetBrains_Mono'] font-bold z-10">
        {frame + 1} / {total}
      </div>
    </div>
  );
}

/** Paint a Fabric preview of a scene's canvas_json. Renders at a scaled
 * target width (preserving intrinsic aspect) so text/rect positions are
 * mathematically consistent — the parent container is also aspect-[9/16],
 * so the canvas element fits exactly. */
function SceneCanvas({ canvasJson, targetWidth = 540 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !canvasJson) return;
    const srcW = canvasJson.width || 1080;
    const srcH = canvasJson.height || 1920;
    const scale = targetWidth / srcW;
    const w = targetWidth;
    const h = Math.round(srcH * scale);
    el.width = w; el.height = h;
    const c = new fabric.StaticCanvas(el, {
      width: w, height: h,
      backgroundColor: canvasJson.background || "#0b0b12",
    });
    (canvasJson.objects || []).forEach((o) => {
      const common = {
        left: (o.left || 0) * scale,
        top: (o.top || 0) * scale,
        opacity: o.opacity ?? 1,
        // Fabric v7 defaults to center origin — force top-left so (left, top)
        // is interpreted as the object's top-left corner, matching how the
        // backend's _build_canvas authors positions.
        originX: "left",
        originY: "top",
        selectable: false, evented: false,
      };
      let fo = null;
      if (o.type === "textbox" || o.type === "text" || o.type === "i-text") {
        // Use Textbox when the backend provided an explicit width so long
        // single-line strings wrap instead of clipping at the canvas edge.
        // Fall back to Text otherwise (lighter object).
        const Cls = o.type === "textbox" && o.width ? fabric.Textbox : fabric.Text;
        const opts = {
          ...common,
          fontSize: Math.max(6, (o.fontSize || 24) * scale),
          fill: o.fill || "#fff",
          fontWeight: o.fontWeight || "normal",
          fontFamily: o.fontFamily || "Inter",
          textAlign: o.textAlign || "left",
          lineHeight: o.lineHeight || 1.16,
          charSpacing: o.charSpacing || 0,
        };
        if (Cls === fabric.Textbox) opts.width = (o.width || 400) * scale;
        fo = new Cls(o.text || "", opts);
      } else if (o.type === "rect") {
        fo = new fabric.Rect({
          ...common,
          width: (o.width || 10) * scale,
          height: (o.height || 10) * scale,
          fill: o.fill || "#333",
          rx: (o.rx || 0) * scale,
          ry: (o.ry || 0) * scale,
        });
      } else if (o.type === "circle") {
        fo = new fabric.Circle({
          ...common,
          radius: (o.radius || 10) * scale,
          fill: o.fill || "#333",
        });
      }
      if (fo) c.add(fo);
    });
    c.renderAll();
    // Fabric sets inline width/height styles on the canvas element that match
    // its intrinsic pixel dimensions (e.g. "540px"). Override them so the
    // canvas scales responsively with its parent container.
    el.style.width = "100%";
    el.style.height = "auto";
    return () => { try { c.dispose(); } catch { /* noop */ } };
  }, [canvasJson, targetWidth]);

  return (
    <canvas
      ref={ref}
      style={{ width: "100%", height: "auto", display: "block" }}
    />
  );
}
